export const DEFAULT_THEME_COLOR = '#3cb377';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const DARK_TEXT = '#1a1a1a';
const CACHE_PREFIX = 'adminTheme:';

export const isValidHex = (value) => typeof value === 'string' && HEX_RE.test(value);

const hexToRgb = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

const rgbToHex = ({ r, g, b }) =>
  '#' + [r, g, b].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('');

const mix = (rgb, target, weight) => ({
  r: rgb.r + (target.r - rgb.r) * weight,
  g: rgb.g + (target.g - rgb.g) * weight,
  b: rgb.b + (target.b - rgb.b) * weight,
});

const luminance = ({ r, g, b }) => {
  const [R, G, B] = [r, g, b].map((c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};

const contrast = (l1, l2) => (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);

export const getThemeVars = (hex) => {
  const rgb = hexToRgb(hex);
  const lum = luminance(rgb);
  const darkLum = luminance(hexToRgb(DARK_TEXT));

  return {
    '--theme-primary': hex,
    '--theme-primary-hover': rgbToHex(mix(rgb, { r: 0, g: 0, b: 0 }, 0.15)),      
    '--theme-primary-light': rgbToHex(mix(rgb, { r: 255, g: 255, b: 255 }, 0.9)),  
    '--theme-primary-rgb': `${rgb.r}, ${rgb.g}, ${rgb.b}`,
    '--theme-on-primary': contrast(lum, 1) >= contrast(lum, darkLum) ? '#ffffff' : DARK_TEXT,
  };
};

const VAR_NAMES = Object.keys(getThemeVars(DEFAULT_THEME_COLOR));

export const applyThemeColor = (hex) => {
  const style = document.documentElement.style;
  if (!isValidHex(hex)) {
    VAR_NAMES.forEach((name) => style.removeProperty(name));
    return;
  }
  Object.entries(getThemeVars(hex)).forEach(([name, value]) => style.setProperty(name, value));
};

const cacheKey = () => {
  const userId = sessionStorage.getItem('userId');
  return userId ? CACHE_PREFIX + userId : null;
};

export const readCachedTheme = () => {
  try {
    const key = cacheKey();
    if (!key) return null;
    const cached = JSON.parse(localStorage.getItem(key));
    return cached && isValidHex(cached.colorCode) ? cached : null;
  } catch {
    return null;
  }
};

export const writeCachedTheme = (theme) => {
  try {
    const key = cacheKey();
    if (key && theme) localStorage.setItem(key, JSON.stringify(theme));
  } catch {
  }
};